"use strict";
import jobBookingCodeBlocks from "./job-booking/Utils/jobBookingCodeBlocks";
import jobBookingUtils from "./job-booking/Utils/jobBookingUtils";
import forms, { shrinkLabels } from "../forms";
import server from "../serverCheck";
import authentication from "../authentication";
import GoogleAddress from "./GoogleAddress";
import toast from "../toast";

const hasProperty = (obj, property) =>
  Object.prototype.hasOwnProperty.call(obj, property);

class GetAddressClass {
  constructor() {
    this.addressData = [];
    this.ready = axios
      .get(`${server.getServer().serverTmsCustomer}/country`, {
        headers: {
          Authorization: "Bearer " + authentication.getDetails("token")
        }
      })
      .then((res) => (this.countries = res.data.data));
  }

  /**
   *
   * @returns {string}
   */
  static get apiKey() {
    return "ak_ml89tcx5JoAvVjgvcREVkSFLslSU3";
  }

  static get hostname() {
    return "api.ideal-postcodes.co.uk";
  }

  /**
   * Request config
   * @returns {Object}
   */
  static get params() {
    return {
      api_key: GetAddressClass.apiKey,
      format: false,
      sort: true,
      expand: true
    };
  }

  /**
   * Get building identifier (if any) for query
   * @param building
   * @param business
   * @returns {*|null}
   */
  filter(building, business) {
    if (building !== undefined) {
      return building;
    } else if (business !== undefined) {
      return business;
    } else {
      return null;
    }
  }

  /**
   * Search for info by postcode
   * @param {string} postCode
   * @param {Function} [onError] called if the lookup fails; defaults to showing the generic failOver toast
   * @param {Function} callBack called with `{ postcode, addresses }` on success, or `null` if the postcode wasn't found/the lookup failed
   */
  doLookup({ postCode, onError = () => this.failOver() }, callBack) {
    const toStr = (v) => (v == null ? "" : String(v));
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const options = {
      url: `postcodes/${encodeURIComponent(postCode)}`,
      baseURL: "https://api.ideal-postcodes.co.uk/v1/",
      method: "GET",
      params: {
        api_key: GetAddressClass.apiKey
      }
    };

    axios(options)
      .then(({ data }) => {
        if (!Array.isArray(data?.result) || data.result.length === 0) {
          return callBack(null);
        }

        /**
         * IMPORTANT:
         * The shape of each address object returned here is relied upon in multiple
         * areas of the application (e.g. address selection, credit checks,
         * translateGetAddress, country resolution, and coordinate expansion).
         *
         * If any fields are added, removed, or renamed in this mapping, corresponding
         * consumers MUST be updated to avoid runtime errors.
         */
        const addresses = data.result.map((a) => ({
          line_1: toStr(a.line_1),
          line_2: toStr(a.line_2),
          line_3: toStr(a.line_3),
          town_or_city: toStr(a.post_town),
          county: toStr(a.county),
          postcode: toStr(a.postcode),
          country: toStr(a.country),
          building_name: toStr(a.building_name || a.organisation_name),
          sub_building_name: toStr(a.sub_building_name),
          latitude: toNum(a.latitude),
          longitude: toNum(a.longitude),

          formatted_address: [
            a.organisation_name,
            a.department_name,
            a.sub_building_name,
            a.building_name,
            a.building_number,
            a.thoroughfare,
            a.post_town,
            a.county,
            a.postcode
          ]
            .filter(Boolean)
            .map(toStr)
        }));

        callBack({
          postcode: toStr(postCode),
          addresses
        });
      })
      .catch(() => {
        onError();
        callBack(null);
      });
  }

  /**
   *
   * @param addressData
   * @returns {HTMLAnchorElement[]|Boolean}
   */
  addressList(addressData) {
    switch (true) {
      case hasProperty(addressData, "postcode"):
        this.addressData = addressData;
        return jobBookingCodeBlocks.getAddressList(addressData.addresses);
      case document
        .querySelector("#confirmation-modal")
        .classList.contains("show"):
        return false;
      case hasProperty(addressData, "status"):
      default:
        return jobBookingCodeBlocks.noAddressList();
    }
  }

  addressListClick({ listItem, getState, postCodeInput }) {
    const addressModal = postCodeInput.closest(".modal-dialog");
    const container = listItem.closest(".js-address-suggest");
    const selectedIndex = listItem.getAttribute("data-address-index");

    switch (true) {
      case listItem.getAttribute("data-disabled") === "true":
      case listItem.getAttribute("data-role") === "cancel":
        this.deleteSuggestions(container, postCodeInput);
        return false;
      default:
        break;
    }

    const formattedAddress = hasProperty(
      this.addressData.addresses[selectedIndex],
      "line_1"
    )
      ? jobBookingUtils.translateGetAddress(this.addressData, selectedIndex)
      : this.addressData.addresses[selectedIndex];

    for (const element of Object.values(
      addressModal.querySelectorAll(".error")
    )) {
      for (const lbl of element.querySelectorAll(".reason")) {
        lbl.remove();
      }
      element.classList.remove("error");
    }

    for (const field of Object.keys(formattedAddress)) {
      if (!getState.supplier) {
        if (getState && hasProperty(getState(), "newAddress")) {
          getState().newAddress[field] = formattedAddress[field];
        }
      }

      const input = addressModal.querySelector(`[name=${field}]`);

      if (input) {
        forms.insertValue(input, formattedAddress[field]);
      }
    }

    this.changed = true;
    this.deleteSuggestions(container, postCodeInput);
  }

  deleteSuggestions(container) {
    jQuery(container).closest(".input-base").find(".dropdown-toggle").click();
    container.innerHTML = "";
  }

  failOver() {
    toast.displayToast({
      type: "warning",
      titleText: "Warning",
      bodyMessageText:
        "There has been an error retrieving address details. <br/>Please try again.</p>"
    });

    return false;
  }

  addressLookup(postCodeInput, container, getState) {
    if (!container) return;

    const postCode = postCodeInput.value;
    const validPostCode = forms.validateInput(postCodeInput);

    const form = postCodeInput.closest("form");
    const addressLine1Input = form.querySelector("[name=addressLine1]");
    const countryIdInput = form.querySelector("[name=countryId]");
    const countryId = parseInt(countryIdInput.value);

    postCodeInput.addEventListener("change", () => {
      shrinkLabels(postCodeInput.closest("label"));
      tailSelect(countryIdInput).reload();
    });

    if (countryId !== 12 && !isNaN(countryId)) {
      const validAddressLine1 = forms.validateInput(addressLine1Input);
      const validCountryId = forms.validateInput(countryIdInput);

      if (validPostCode && validAddressLine1 && validCountryId) {
        const country = () => {
          if (typeof getState === "object") {
            return getState.countries.state.options[countryId - 1];
          } else {
            return getState().countries[countryId];
          }
        };

        const addressData = {
          addressLine1: addressLine1Input.value,
          country: country().name,
          postCode
        };

        GoogleAddress.addressLookup(addressData, this, (addressList) => {
          this.deleteSuggestions(container, postCodeInput);
          this.renderItems(addressList, getState, postCodeInput, container);
        });
      }

      return true;
    } else if (countryId === 12 || isNaN(countryId)) {
      if (validPostCode) {
        /** go to GetAddress */
        this.doLookup({ postCode }, (response) => {
          this.deleteSuggestions(container, postCodeInput);

          if (!response) return false;

          const addressList = this.addressList(response);

          this.renderItems(addressList, getState, postCodeInput, container);
        });
      }

      return true;
    }
  }

  renderItems(addressList, getState, postCodeInput, container) {
    if (addressList) {
      for (const item of addressList) {
        item.addEventListener("click", (e) => {
          const listItem = e.target;
          this.addressListClick({ listItem, getState, postCodeInput });
        });
        container.appendChild(item);
      }
      container.classList.add("show");
    }
  }

  static get Instance() {
    return new GetAddressClass();
  }
}

export default GetAddressClass;
